import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Plus, X } from "lucide-react";

const canEditContact = (hasCap) =>
  hasCap("crm.edit_own") || hasCap("crm.edit_team") || hasCap("crm.edit_any");

/* Current Customer Profile editor — the ONE canonical customer-details
   editor (Customer 360). Raw lead-intake history is shown separately by
   LeadFormInfo and is never touched here. Dedup is enforced server-side:
   a phone/email already owned by another active customer comes back as a
   409 conflict with a link to that record for the Merge workflow. */
export const CustomerProfileEditButton = ({ contact, contactMethods, onSaved }) => {
  const { hasCap } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [form, setForm] = useState({ full_name: "", city: "", phones: [], emails: [] });

  useEffect(() => {
    if (!open) return;
    setConflict(null);
    // Prefer the structured methods from /contacts/{id}/full; otherwise
    // fall back to the raw arrays on the contact, then to the flat legacy
    // fields — so the editor works wherever it's mounted, with or without
    // the contact_methods payload.
    const srcPhones = contactMethods?.phones || contact?.phones || [];
    const srcEmails = contactMethods?.emails || contact?.emails || [];
    const phones = srcPhones.map((p) => ({
      value: p.value || "", is_primary: !!p.is_primary, is_whatsapp: !!p.is_whatsapp, label: p.label || "" }));
    const emails = srcEmails.map((e) => ({
      value: e.value || "", is_primary: !!e.is_primary, label: e.label || "" }));
    setForm({
      full_name: contact?.full_name || "", city: contact?.city || "",
      phones: phones.length ? phones : [{
        value: contact?.primary_phone || "", is_primary: true,
        is_whatsapp: !contact?.whatsapp_phone || contact?.whatsapp_phone === contact?.primary_phone,
        label: "" }].concat(contact?.whatsapp_phone && contact.whatsapp_phone !== contact.primary_phone
          ? [{ value: contact.whatsapp_phone, is_primary: false, is_whatsapp: true, label: "whatsapp" }] : []),
      emails: emails.length ? emails : (contact?.email ? [{ value: contact.email, is_primary: true, label: "" }] : []),
    });
  }, [open, contact, contactMethods]);

  if (!canEditContact(hasCap) || (contact && ["merged", "archived"].includes(contact.status))) return null;

  const setPhone = (i, patch) => setForm((f) => ({
    ...f, phones: f.phones.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const setPrimaryPhone = (i) => setForm((f) => ({
    ...f, phones: f.phones.map((p, j) => ({ ...p, is_primary: j === i })) }));
  const setEmail = (i, patch) => setForm((f) => ({
    ...f, emails: f.emails.map((e, j) => (j === i ? { ...e, ...patch } : e)) }));
  const setPrimaryEmail = (i) => setForm((f) => ({
    ...f, emails: f.emails.map((e, j) => ({ ...e, is_primary: j === i })) }));

  const save = async () => {
    setBusy(true); setConflict(null);
    try {
      const phones = form.phones.filter((p) => p.value.trim());
      const emails = form.emails.filter((e) => e.value.trim());
      const { data } = await api.patch(`/contacts/${contact.contact_id}`, {
        full_name: form.full_name.trim() || null,
        city: form.city,
        phones: phones.map((p) => ({ value: p.value.trim(), is_primary: !!p.is_primary,
          is_whatsapp: !!p.is_whatsapp, label: p.label || null })),
        emails: emails.map((e) => ({ value: e.value.trim(), is_primary: !!e.is_primary,
          label: e.label || null })),
      });
      toast.success("Customer profile updated");
      setOpen(false);
      onSaved?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "identity_conflict") {
        setConflict(d.conflict);
        toast.error(d.message || "That contact detail belongs to another customer");
      } else {
        toast.error(errDetail(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button data-testid="c360-edit-profile" size="sm" variant="outline" className="h-8 text-xs"
        onClick={() => setOpen(true)}>
        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit profile
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="c360-edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit customer profile</DialogTitle>
            <DialogDescription>
              This updates the current customer record only. The original lead-form
              submissions are kept unchanged in Lead intake history.
            </DialogDescription>
          </DialogHeader>

          {conflict && (
            <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-3 text-xs space-y-1"
              data-testid="c360-edit-conflict">
              <p className="font-medium text-[hsl(var(--danger))]">This detail already belongs to another customer</p>
              <p>
                <Link className="underline decoration-dotted hover:text-primary" to={`/customers/${conflict.contact_id}`}
                  onClick={() => setOpen(false)}>
                  {conflict.full_name} ({conflict.customer_id})
                </Link>{" "}— nothing was changed. Use the Merge workflow if these are the same person.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Full name</Label>
              <Input data-testid="c360-edit-name" value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">City</Label>
              <Input data-testid="c360-edit-city" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Phone numbers</Label>
              {form.phones.map((p, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`c360-edit-phone-${i}`}>
                  <Input className="h-8 text-sm" placeholder="Phone number" value={p.value}
                    onChange={(e) => setPhone(i, { value: e.target.value })} data-testid={`c360-edit-phone-input-${i}`} />
                  <label className="flex items-center gap-1 text-[11px] whitespace-nowrap">
                    <input type="radio" name="primaryPhone" checked={!!p.is_primary}
                      onChange={() => setPrimaryPhone(i)} data-testid={`c360-edit-phone-primary-${i}`} /> Primary
                  </label>
                  <label className="flex items-center gap-1 text-[11px] whitespace-nowrap">
                    <input type="checkbox" checked={!!p.is_whatsapp}
                      onChange={(e) => setPhone(i, { is_whatsapp: e.target.checked })}
                      data-testid={`c360-edit-phone-wa-${i}`} /> WhatsApp
                  </label>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => setForm((f) => ({ ...f, phones: f.phones.filter((_, j) => j !== i) }))}
                    data-testid={`c360-edit-phone-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
                onClick={() => setForm((f) => ({ ...f, phones: [...f.phones, { value: "", is_primary: !f.phones.length, is_whatsapp: false, label: "" }] }))}
                data-testid="c360-edit-add-phone">
                <Plus className="h-3 w-3 mr-1" /> Add phone
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Email addresses</Label>
              {form.emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`c360-edit-email-${i}`}>
                  <Input className="h-8 text-sm" placeholder="name@email.com" value={em.value}
                    onChange={(e) => setEmail(i, { value: e.target.value })} data-testid={`c360-edit-email-input-${i}`} />
                  <label className="flex items-center gap-1 text-[11px] whitespace-nowrap">
                    <input type="radio" name="primaryEmail" checked={!!em.is_primary}
                      onChange={() => setPrimaryEmail(i)} data-testid={`c360-edit-email-primary-${i}`} /> Primary
                  </label>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => setForm((f) => ({ ...f, emails: f.emails.filter((_, j) => j !== i) }))}
                    data-testid={`c360-edit-email-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
                onClick={() => setForm((f) => ({ ...f, emails: [...f.emails, { value: "", is_primary: !f.emails.length, label: "" }] }))}
                data-testid="c360-edit-add-email">
                <Plus className="h-3 w-3 mr-1" /> Add email
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="c360-edit-save" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
